import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Camera, ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grid lg:grid-cols-2" data-testid="admin-login-page">
      <div className="hidden lg:flex relative bg-secondary text-secondary-foreground grain-overlay p-12 flex-col justify-between overflow-hidden">
        <Link to="/" className="flex items-center gap-2 relative z-10" data-testid="login-logo">
          <span className="w-8 h-8 rounded-sm bg-primary text-primary-foreground grid place-items-center">
            <Camera size={16} strokeWidth={2.4} />
          </span>
          <span className="font-display font-bold text-lg">
            Snap<span className="text-primary">.</span>Send<span className="text-primary">.</span>Repair
          </span>
        </Link>
        <div className="relative z-10">
          <div className="text-xs uppercase tracking-widest text-primary mb-3">Admin inbox</div>
          <h1 className="font-display text-5xl xl:text-6xl font-bold leading-none tracking-tight">
            Every request.<br />
            <span className="text-primary">One place.</span>
          </h1>
          <p className="mt-6 text-white/70 max-w-md leading-relaxed">
            See who's asking, sort by status, update as jobs move from new → quoted → booked → done.
          </p>
        </div>
        <div className="relative z-10 flex items-center gap-2 text-xs text-white/60">
          <ShieldCheck size={14} className="text-primary" /> Private area — team members only
        </div>
        <div className="absolute -bottom-20 -right-16 watermark-num text-[380px] text-white/5 pointer-events-none select-none">
          admin
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md space-y-6"
          data-testid="admin-login-form"
        >
          <div>
            <div className="text-xs uppercase tracking-widest text-primary mb-2">Sign in</div>
            <h2 className="font-display text-3xl font-bold text-secondary">Admin access</h2>
            <p className="mt-2 text-sm text-muted-foreground">Use the credentials set for your team.</p>
          </div>

          <label className="block">
            <span className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Email</span>
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="field w-full rounded-sm px-4 py-3"
              placeholder="you@snapsendrepair.com.au"
              data-testid="login-email"
              autoComplete="email"
            />
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Password</span>
            <input
              required
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="field w-full rounded-sm px-4 py-3"
              placeholder="••••••••"
              data-testid="login-password"
              autoComplete="current-password"
            />
          </label>

          <button
            type="submit"
            disabled={busy}
            className="btn-lift w-full bg-primary text-primary-foreground rounded-sm px-6 py-3.5 font-medium inline-flex items-center justify-center gap-2 disabled:opacity-70"
            data-testid="login-submit"
          >
            {busy ? <><Loader2 className="animate-spin" size={18} /> Signing in…</> : <>Sign in <ArrowRight size={18} /></>}
          </button>

          <Link to="/" className="block text-center text-xs text-muted-foreground hover:text-primary" data-testid="back-to-site">
            ← Back to public site
          </Link>
        </motion.form>
      </div>
    </div>
  );
}
